import React, { useEffect, useRef, useState, useCallback } from 'react'
import { FULL_VIDEO_VIEW_HEIGHT, VideoPlayerOptions } from '../../../../Constant/video'
import { useDynamicTheme } from '../../../../Hooks/Theme/useDynamicTheme'
import {
  loadBrightcoveScript,
  createVideoElement,
  getPlayerInstance,
  waitForPlayerReady,
  configurePlayerOptions,
  ensurePlayerSize,
  updateVideoElementSize,
  loadVideoInPlayer,
  cleanupPlayer,
  generatePlayerId,
  attachPlayerEvents,
  calcDimensionsFromAspectRatio,
} from '../../../../Utils'
import { VideoElement, VideoErrorContainer } from '../styles'

type VideoPlayerProps = {
  videoId: string
  options: VideoPlayerOptions
  onVideoStart: () => void
  logVideoComplete: () => void
  onVideoComplete?: () => void
  onVideoPause: (watchedTime: number) => void
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  videoId,
  options,
  onVideoStart,
  logVideoComplete,
  onVideoComplete,
  onVideoPause,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoNodeRef = useRef<HTMLElement | null>(null)
  const playerRef = useRef<any>(null)
  const stableIdRef = useRef(generatePlayerId())
  const mounted = useRef(true)
  const startedOnce = useRef(false)

  const [error, setError] = useState<string | null>(null)

  const { isMobile } = useDynamicTheme()
  const videoWidth = `${calcDimensionsFromAspectRatio(
    options.aspectRatio,
    FULL_VIDEO_VIEW_HEIGHT,
    isMobile
  )}vh`

  const ensureSize = useCallback(() => {
    ensurePlayerSize(videoNodeRef.current, playerRef.current)
  }, [])

  const attachEvents = useCallback(
    (player: any) => {
      attachPlayerEvents(
        player,
        {
          onVideoStart,
          logVideoComplete,
          onVideoComplete,
          onVideoPause,
        },
        startedOnce
      )
    },
    [logVideoComplete, onVideoComplete, onVideoPause, onVideoStart]
  )

  useEffect(() => {
    mounted.current = true
    if (!containerRef.current || !videoId) return
    let cancelled = false

    const mount = async () => {
      try {
        const el = createVideoElement(stableIdRef.current, videoId, videoWidth)
        containerRef.current!.appendChild(el)
        videoNodeRef.current = el

        await loadBrightcoveScript()
        if (cancelled || !mounted.current) return

        const player = getPlayerInstance(stableIdRef.current, el)
        if (!player) throw new Error('Brightcove player instance not found')

        await waitForPlayerReady(player)
        playerRef.current = player

        configurePlayerOptions(player, options)
        attachEvents(player)
        ensureSize()
      } catch (e) {
        if (!cancelled) setError('Failed to load video player')
      }
    }

    mount()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const player = playerRef.current
    const el = videoNodeRef.current
    if (!player || !el || !videoId) return

    // Reset the startedOnce flag when loading new video
    startedOnce.current = false

    el.setAttribute('data-video-id', videoId)
    loadVideoInPlayer(player, videoId)
  }, [videoId])

  // Reattach events when callbacks change
  useEffect(() => {
    const player = playerRef.current
    if (!player) return
    attachEvents(player)
  }, [attachEvents])

  useEffect(() => {
    updateVideoElementSize(videoNodeRef.current, videoWidth)
  }, [videoWidth])

  useEffect(() => {
    return () => {
      mounted.current = false
      cleanupPlayer(playerRef.current)
      playerRef.current = null
    }
  }, [])

  if (!videoId) return null

  if (error) {
    return <VideoErrorContainer>{error}</VideoErrorContainer>
  }

  return <VideoElement ref={containerRef} $videoWidth={videoWidth} />
}
